import { useMemo, useState, useEffect, useRef } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTheme } from '@/components/ThemeProvider';
import { formatCurrency as fmtCurrency, useCurrency } from '@/lib/currency';

// Chart-scale labels drop cents; ledger labels keep them.
const formatChartCurrency = (value, code) =>
  fmtCurrency(value, code).replace(/[.,]\d{2}(?!\d)/, '');

const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const makeTooltip = (code) => function CurrencyAwareTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-xs text-muted-foreground mb-1">{formatDate(data.date)}</p>
        <p className="text-sm font-medium">{data.name}</p>
        <p className={`font-mono text-sm ${data.amount > 0 ? 'text-green-500' : ''}`}>
          {data.amount > 0 ? '+' : ''}{fmtCurrency(data.amount, code)}
        </p>
        <p className="font-mono text-base font-bold mt-1">
          Balance: {fmtCurrency(data.balance, code)}
        </p>
      </div>
    );
  }
  return null;
};

function SizeGuard({ children, className, testId }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setReady(true);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className} data-testid={testId}>
      {ready ? children : null}
    </div>
  );
}

export const BalanceChart = ({ entries, startingBalance, minAlert, maxAlert }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => formatChartCurrency(v, currencyCode);
  const CustomTooltip = useMemo(() => makeTooltip(currencyCode), [currencyCode]);

  const chartData = useMemo(() => {
    // Stable-sort: within the same date put expenses (negative)
    // before income (positive) so the chart shows the dip-then-rise
    // pattern.  After re-ordering we recalculate the running balance.
    const sorted = [...entries].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      const aSign = a.amount < 0 ? 0 : 1;
      const bSign = b.amount < 0 ? 0 : 1;
      return aSign - bSign;
    });

    // Recalculate running balance after reordering
    let balance = startingBalance;
    const dateCounts = {};
    return sorted.map((entry) => {
      balance = Math.round((balance + entry.amount) * 100) / 100;
      const count = dateCounts[entry.date] || 0;
      dateCounts[entry.date] = count + 1;
      return {
        xKey: count === 0 ? entry.date : `${entry.date}#${count}`,
        date: entry.date,
        balance,
        name: entry.name,
        amount: entry.amount,
      };
    });
  }, [entries, startingBalance]);

  const { minBalance, maxBalance } = useMemo(() => {
    if (chartData.length === 0) return { minBalance: 0, maxBalance: 0 };
    
    let min = chartData[0].balance;
    let max = chartData[0].balance;
    
    for (let i = 1; i < chartData.length; i++) {
      if (chartData[i].balance < min) min = chartData[i].balance;
      if (chartData[i].balance > max) max = chartData[i].balance;
    }
    
    // Expand range to include alert thresholds so reference lines are visible
    if (minAlert !== undefined && minAlert !== null && minAlert < min) {
      min = minAlert;
    }
    if (maxAlert !== undefined && maxAlert !== null && maxAlert > max) {
      max = maxAlert;
    }
    
    // Add some padding
    const padding = (max - min) * 0.1;
    return {
      minBalance: Math.floor((min - padding) / 1000) * 1000,
      maxBalance: Math.ceil((max + padding) / 1000) * 1000,
    };
  }, [chartData, minAlert, maxAlert]);

  const gridColor = isDark ? '#334155' : '#e2e8f0';
  const textColor = isDark ? '#94a3b8' : '#64748b';
  const areaColor = isDark ? '#3b82f6' : '#2563eb';
  const areaGradientStart = isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(37, 99, 235, 0.2)';
  const areaGradientEnd = isDark ? 'rgba(59, 130, 246, 0.0)' : 'rgba(37, 99, 235, 0.0)';

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        No data to display
      </div>
    );
  }

  return (
    <SizeGuard className="w-full h-[400px] md:h-[500px]" testId="balance-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <defs>
            <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaGradientStart} />
              <stop offset="100%" stopColor={areaGradientEnd} />
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
          
          <XAxis
            dataKey="xKey"
            tickFormatter={(val) => formatDate(val.split('#')[0])}
            tick={{ fill: textColor, fontSize: 11 }}
            axisLine={{ stroke: gridColor }}
            tickLine={{ stroke: gridColor }}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fill: textColor, fontSize: 11 }}
            axisLine={{ stroke: gridColor }}
            tickLine={{ stroke: gridColor }}
            domain={[minBalance, maxBalance]}
            width={80}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          {/* Min balance alert line */}
          {minAlert !== undefined && minAlert !== null && (
            <ReferenceLine
              y={minAlert}
              stroke="#ef4444"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `Low: ${formatCurrency(minAlert)}`,
                fill: '#ef4444',
                fontSize: 11,
                position: 'insideBottomRight',
              }}
            />
          )}
          
          {/* Max balance alert line */}
          {maxAlert !== undefined && maxAlert !== null && (
            <ReferenceLine
              y={maxAlert}
              stroke="#eab308"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `High: ${formatCurrency(maxAlert)}`,
                fill: '#eab308',
                fontSize: 11,
                position: 'insideTopRight',
              }}
            />
          )}
          
          <Area
            type="monotone"
            dataKey="balance"
            stroke={areaColor}
            strokeWidth={2}
            fill="url(#balanceGradient)"
            dot={false}
            activeDot={{
              r: 6,
              fill: areaColor,
              stroke: isDark ? '#0f172a' : '#ffffff',
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </SizeGuard>
  );
};
